import type { AxiosError } from "axios";
import api from "./api/api";

function getErrorResponse(error: unknown) {
  return (error as AxiosError)?.response;
}

export type DeskProducaoStatus =
  | "orcado"
  | "aprovado"
  | "aguardando_pagamento"
  | "pago"
  | "cancelado";

export type DeskProducaoCategoria = {
  id: number;
  nome: string;
  cor?: string | null;
  ativo: boolean;
};

export type DeskProducaoFornecedor = {
  id: number;
  nome: string;
  name_fantasy?: string | null;
  documento?: string | null;
  email?: string | null;
  telefone?: string | null;
  tipo_servico?: string | null;
};

export type DeskProducaoAnexo = {
  id: number;
  addesk_id?: number;
  empresa_id?: number;
  tipo: string;
  file?: {
    id: number;
    name: string;
    path: string;
  };
};

export type DeskProducaoLancamento = {
  id: number;
  fornecedor_empresa_id: number;
  categoria_id: number;
  descricao: string;
  centro_custo?: string | null;
  departamento_responsavel?: string | null;
  data_contratacao?: string | null;
  data_vencimento: string;
  data_pagamento?: string | null;
  valor_orcado: number;
  valor_aprovado?: number | null;
  valor_final?: number | null;
  forma_pagamento?: string | null;
  parcelamento?: string | null;
  numero_documento?: string | null;
  status: DeskProducaoStatus;
  observacoes?: string | null;
  fornecedor?: DeskProducaoFornecedor;
  categoria?: DeskProducaoCategoria;
  anexos?: DeskProducaoAnexo[];
};

export type DeskProducaoResumoItem = {
  id: number;
  nome: string;
  total: number;
  percentual: number;
  cor?: string | null;
};

export type DeskProducaoFluxoItem = {
  data: string;
  previsto: number;
  pago: number;
};

export type DeskProducaoDashboard = {
  valor_proposta: number;
  valor_vendido: number;
  imposto_percentual: number;
  valor_imposto: number;
  incentivo_percentual: number;
  valor_incentivo: number;
  custo_total_crl: number;
  percentual_custo_crl: number;
  custo_total_producao: number;
  valor_pago: number;
  valor_a_pagar: number;
  saldo_disponivel: number;
  percentual_orcamento_comprometido: number;
  crl: number;
  custos_por_categoria: DeskProducaoResumoItem[];
  custos_por_fornecedor: DeskProducaoResumoItem[];
  fluxo_pagamentos: DeskProducaoFluxoItem[];
};

export type DeskProducaoOrcamento = {
  id: number;
  addesk_id: number;
  empresa_id: number;
  valor: number;
  incentivo_percentual: number;
};

export type DeskProducaoData = {
  desk_id: number;
  statuses: DeskProducaoStatus[];
  categorias: DeskProducaoCategoria[];
  lancamentos: DeskProducaoLancamento[];
  pagamentos: DeskProducaoLancamento[];
  orcamento?: DeskProducaoOrcamento | null;
  dashboard: DeskProducaoDashboard;
};

export type DeskProducaoFilters = {
  status?: string;
  fornecedor_empresa_id?: number | string;
  categoria_id?: number | string;
  periodo_inicio?: string;
  periodo_fim?: string;
};

export const ServiceDeskProducao = {
  getResumo: async (deskId: number | string, filters?: DeskProducaoFilters) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, String(value));
        }
      });
      const query = params.toString();
      return await api.get<{ status: boolean; data: DeskProducaoData }>(
        `/desks/producao/${deskId}${query ? `?${query}` : ""}`,
      );
    } catch (error) {
      return getErrorResponse(error);
    }
  },
  createLancamento: async (deskId: number | string, payload: FormData) => {
    try {
      return await api.post(`/desks/producao/${deskId}/lancamentos`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      return getErrorResponse(error);
    }
  },
  updateLancamento: async (
    deskId: number | string,
    lancamentoId: number | string,
    payload: FormData,
  ) => {
    try {
      return await api.put(
        `/desks/producao/${deskId}/lancamentos/${lancamentoId}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    } catch (error) {
      return getErrorResponse(error);
    }
  },
  deleteLancamento: async (
    deskId: number | string,
    lancamentoId: number | string,
  ) => {
    try {
      return await api.delete(
        `/desks/producao/${deskId}/lancamentos/${lancamentoId}`,
      );
    } catch (error) {
      return getErrorResponse(error);
    }
  },
  marcarPagamento: async (
    deskId: number | string,
    lancamentoId: number | string,
    payload: FormData,
  ) => {
    try {
      return await api.patch(
        `/desks/producao/${deskId}/lancamentos/${lancamentoId}/pagamento`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    } catch (error) {
      return getErrorResponse(error);
    }
  },
  updateOrcamento: async (
    deskId: number | string,
    payload: {
      valor: number | string;
      incentivo_percentual: number | string;
    },
  ) => {
    try {
      return await api.patch(`/desks/producao/${deskId}/orcamento`, payload);
    } catch (error) {
      return getErrorResponse(error);
    }
  },
  createCategoria: async (payload: {
    nome: string;
    cor?: string;
    ativo?: boolean;
  }) => {
    try {
      return await api.post("/desks/producao/categorias", payload);
    } catch (error) {
      return getErrorResponse(error);
    }
  },
  updateCategoria: async (
    categoriaId: number | string,
    payload: { nome: string; cor?: string; ativo?: boolean },
  ) => {
    try {
      return await api.put(`/desks/producao/categorias/${categoriaId}`, payload);
    } catch (error) {
      return getErrorResponse(error);
    }
  },
};
